import { useState, type FormEvent } from "react";
import { Plus } from "lucide-react";
import { actions, listContracts, listLocations, listReorderTemplates } from "@/lib/api";
import type { Contract } from "@/lib/types";
import { displayDate, money, titleCase } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select } from "@/components/ui/select";
import { useData, matches, reportActionError, type ViewProps, type DialogProps } from "@/lib/app-view";
import { PageHeader } from "@/components/page-header";
import { Empty, Loading, ErrorState } from "@/components/empty-state";
import { Table } from "@/components/data-table";
import { FieldError } from "@/components/field-error";

export function Reorders({ organisation, refresh, query, can, changed }: ViewProps) {
  const [
    { data: rows, error, loading },
    { data: contracts },
    { data: locations },
  ] = [
    useData(
      () => listReorderTemplates(organisation.id),
      [organisation.id, refresh],
    ),
    useData(() => listContracts(organisation.id), [organisation.id, refresh]),
    useData(() => listLocations(organisation.id), [organisation.id, refresh]),
  ];
  const [creating, setCreating] = useState(false);
  const [busy, setBusy] = useState("");
  if (loading) return <Loading />;
  if (error || !rows) return <ErrorState message={error} />;
  const filtered = rows.filter((row) =>
    matches(
      query,
      row.title,
      row.frequency,
      row.locations?.name,
      row.contract_items?.description,
    ),
  );
  async function raise(id: string) {
    setBusy(id);
    try {
      await actions.raiseContractReorder(organisation.id, id);
      changed();
    } catch (reason) {
      reportActionError(reason);
    } finally {
      setBusy("");
    }
  }
  return (
    <>
      <PageHeader
        eyebrow="REPEAT PROCUREMENT"
        title="Reorder plans"
        description="Turn approved contracted rates into repeatable requisitions without re-keying commercial terms."
      >
        {can("requisition") && (
          <Button onClick={() => setCreating(true)}>
            <Plus className="size-4" />
            New reorder plan
          </Button>
        )}
      </PageHeader>
      {filtered.length ? (
        <Table
          heads={[
            "Plan",
            "Item",
            "Location",
            "Frequency",
            "Next due",
            "Action",
          ]}
        >
          {filtered.map((row) => (
            <tr key={row.id}>
              <td>
                <strong>{row.title}</strong>
                <small className="block text-slate-500">
                  {row.auto_submit ? "Auto-submit" : "Draft for review"}
                </small>
              </td>
              <td>
                {row.contract_items?.description}
                <small className="block text-slate-500">
                  {row.quantity} {row.contract_items?.unit} ·{" "}
                  {row.contract_items?.contracts?.currency}
                </small>
              </td>
              <td>{row.locations?.name}</td>
              <td>{titleCase(row.frequency)}</td>
              <td>{displayDate(row.next_due_on)}</td>
              <td>
                {can("requisition") && (
                  <Button
                    size="sm"
                    disabled={busy === row.id}
                    onClick={() => raise(row.id)}
                  >
                    Raise now
                  </Button>
                )}
              </td>
            </tr>
          ))}
        </Table>
      ) : (
        <Empty
          title="No reorder plans"
          description="Activate a contract, then create a reusable reorder plan."
        />
      )}
      <ReorderDialog
        open={creating}
        onOpenChange={setCreating}
        organisationId={organisation.id}
        contracts={contracts || []}
        locations={locations || []}
        onDone={changed}
      />
    </>
  );
}

function ReorderDialog({
  open,
  onOpenChange,
  organisationId,
  contracts,
  locations,
  onDone,
}: DialogProps & {
  contracts: Contract[];
  locations: Awaited<ReturnType<typeof listLocations>>;
}) {
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const items = contracts
    .filter((c) => c.status === "active")
    .flatMap((c) =>
      (c.contract_items || [])
        .filter((i) => i.active)
        .map((i) => ({
          ...i,
          label: `${c.contract_no} · ${i.description} · ${money(i.contracted_price, c.currency)}`,
        })),
    );
  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    setBusy(true);
    setError("");
    try {
      await actions.createReorderTemplate(organisationId, {
        p_location_id: form.get("location"),
        p_contract_item_id: form.get("item"),
        p_title: form.get("title"),
        p_quantity: Number(form.get("quantity")),
        p_frequency: form.get("frequency"),
        p_next_due_on: form.get("nextDue") || null,
        p_auto_submit: Boolean(form.get("autoSubmit")),
      });
      onOpenChange(false);
      onDone();
    } catch (reason) {
      setError((reason as Error).message);
    } finally {
      setBusy(false);
    }
  }
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader eyebrow="REPEAT PROCUREMENT" title="New reorder plan" />
        <form onSubmit={submit} className="grid gap-4 p-6">
          <Label>
            Plan name
            <Input name="title" required />
          </Label>
          <Label>
            Contracted item
            <Select name="item" required>
              {items.map((item) => (
                <option key={item.id} value={item.id}>
                  {item.label}
                </option>
              ))}
            </Select>
          </Label>
          <div className="grid gap-4 md:grid-cols-2">
            <Label>
              Location
              <Select name="location" required>
                {locations.map((location) => (
                  <option key={location.id} value={location.id}>
                    {location.name}
                  </option>
                ))}
              </Select>
            </Label>
            <Label>
              Quantity
              <Input
                name="quantity"
                type="number"
                min="0.01"
                step="0.01"
                required
              />
            </Label>
            <Label>
              Frequency
              <Select name="frequency">
                <option value="manual">Manual</option>
                <option value="weekly">Weekly</option>
                <option value="monthly">Monthly</option>
                <option value="quarterly">Quarterly</option>
              </Select>
            </Label>
            <Label>
              Next due
              <Input name="nextDue" type="date" />
            </Label>
          </div>
          <label className="flex items-center gap-3 text-sm">
            <input name="autoSubmit" type="checkbox" />
            Submit generated requisitions automatically
          </label>
          <FieldError>{error}</FieldError>
          <div className="flex justify-end gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
            >
              Cancel
            </Button>
            <Button disabled={busy} type="submit">
              Save reorder plan
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
